import { parsePositiveInteger } from 'src/shared/urlValidators';
import moment from 'moment';
import { capFirst } from '@tovia/man-app-utils/lib/helpers/converters';
import { SORT_TYPES as MODEL_SORT_TYPES } from 'src/shared/constants/models';
import { SORT_TYPES as GALLERY_SORT_TYPES } from 'src/shared/constants/galleries';
import { SORT_TYPES as MOVIE_SORT_TYPES } from 'src/shared/constants/movies';
import { SORT_TYPES as PHOTOGRAPHER_SORT_TYPES } from 'src/shared/constants/photographers';
import { SORT_TYPES as SEARCH_SORT_TYPES } from 'src/shared/constants/search';
import { modelLinks, parseSlug } from './converters';
import { RootState } from '../redux/modules/reducer';
import { BreadCrumb } from '../containers/BreadCrumbs/BreadCrumbs';
import { match } from 'react-router';
import { canViewSfwImages, getSfwPageNumberForImage, getSfwTotalPages, getSfwTotalImages } from 'src/shared/sfwImages';
import { getSfwPagePath } from 'src/client/containers/SfwImages/paths';

const configFilter = (arr) => (sort) => (Array.isArray(arr) ? arr.includes(sort.id) : true);
export const getModelSortTypes = (config) => MODEL_SORT_TYPES.filter(configFilter(config.modelSort));
export const getGallerySortTypes = (config) => GALLERY_SORT_TYPES.filter(configFilter(config.gallerySort));
export const getMovieSortTypes = (config) => MOVIE_SORT_TYPES.filter(configFilter(config.movieSort));
export const getPhotographerSortTypes = (config) =>
  PHOTOGRAPHER_SORT_TYPES.filter(configFilter(config.photographerSort));

type RouteParams = {
  category?: string;
  country?: string;
  displayName?: string;
  modelName?: string;
  month?: string;
  number?: string;
  pageOrDate?: string;
  photographerName?: string;
  sortBy?: string;
  tab?: string;
  type?: string;
  year?: string;
};

type BreadcrumbParams = { match: match<RouteParams>; state: RootState };

export type LocalRoute = {
  breadcrumb?: BreadCrumb | ((params: BreadcrumbParams) => false | null | BreadCrumb | BreadCrumb[]);
  oldPath?: string;
  path: string | string[];
  value?: string;
};

type BreadcrumbSort = { id: string; title: string };

const getSortTitle = (sortTypes: BreadcrumbSort[], sortBy?: string) =>
  sortTypes.find((sort) => sort.id === sortBy)?.title;

const formatSortTitle = (title?: string) => (title ? `(${title})` : '');

const getYearBreadcrumbs = (section: BreadCrumb, year?: string): BreadCrumb[] => {
  const breadcrumbs = [section];
  if (year) {
    breadcrumbs.push({ text: moment(year, 'YYYY').format('YYYY') });
  }
  return breadcrumbs;
};

const getTypeBreadcrumbs = (section: BreadCrumb, type?: string): BreadCrumb[] => [
  section,
  { text: capFirst(type ?? '') },
];

export const routes = {
  sfwImage: {
    breadcrumb: ({ match, state }: BreadcrumbParams) => {
      const number = parsePositiveInteger(match.params.number);
      if (
        !state.auth.loaded ||
        !canViewSfwImages(state.auth.user) ||
        number === null ||
        number > getSfwTotalImages(state.app.config)
      ) {
        return null;
      }
      return [
        { text: 'SFW Images', url: getSfwPagePath(getSfwPageNumberForImage(number)) },
        { text: `Image ${number}` },
      ];
    },
    path: '/sfw-images/image/:number',
  },
  sfwImages: {
    breadcrumb: ({ state }: BreadcrumbParams) =>
      state.auth.loaded && canViewSfwImages(state.auth.user) && getSfwTotalPages(state.app.config) > 0
        ? { text: 'SFW Images' }
        : null,
    path: '/sfw-images/:page?',
  },
  galleries: {
    breadcrumb: {
      text: 'Galleries',
      url: '/galleries',
    },
    path: ['/galleries', '/images', '/photo-archive'],
    routes: {
      gallery: {
        breadcrumb: ({ state }: BreadcrumbParams) => {
          const gallery = state.gallery.item;
          if (gallery) {
            return [
              {
                node: modelLinks(gallery.models),
              },
              {
                text: gallery.name,
              },
            ];
          }
          return false;
        },
        path: '/model/:modelName/gallery/:date/:galleryName/:page?',
      },
      myGalleries: {
        breadcrumb: {
          text: 'My Galleries',
        },
        path: '/galleries/my/:sortBy?/:page?',
        value: 'PPSGalleries',
      },
      galleries: {
        breadcrumb: ({ match }: BreadcrumbParams) => {
          const sortTitle = getSortTitle(GALLERY_SORT_TYPES, match.params.sortBy);

          if (!sortTitle) {
            return null;
          }

          return {
            text: sortTitle,
          };
        },
        path: '/galleries/:sortBy?/:page?',
        value: 'Galleries',
      },
      images: {
        breadcrumb: {
          text: 'Top Images',
        },
        path: '/images',
        value: 'Images',
      },
      botd: {
        breadcrumb: ({ state }: BreadcrumbParams) => {
          const text = (state.app.config.galleries.botd || {}).active ? 'Beauty of the Day' : 'Photo of the Day';
          return {
            text,
          };
        },
        path: '/photo-archive/:page?',
        value: 'Beauty of the Day',
      },
    },
  },
  images: {
    path: '/model',
    routes: {
      image: { path: '/model/:modelName/:mediaType/:date/:galleryName/image/:imageNumber/:quality?' },
      grid: { path: '/model/:modelName/:mediaType/:date/:galleryName/image/:imageNumber/grid/:galleryPageNumber?' },
    },
  },
  modelsAndArtists: {
    breadcrumb: {
      text: 'Models & Artists',
      url: '/models',
    },
    path: ['/models', '/photographers'],
    routes: {
      modelMovies: {
        breadcrumb: ({ match }: BreadcrumbParams) => {
          const sortTitle = getSortTitle(MOVIE_SORT_TYPES, match.params.sortBy);
          const slug = parseSlug(match.params.modelName);

          return {
            text: `${slug} - Movies ${formatSortTitle(sortTitle)}`,
          };
        },
        path: '/model/:modelName/:tab(movies)/:sortBy?/:page?',
        value: 'movies',
      },
      modelPhotos: {
        breadcrumb: ({ match }: BreadcrumbParams) => {
          const sortTitle = getSortTitle(MOVIE_SORT_TYPES, match.params.sortBy);
          const slug = parseSlug(match.params.modelName);

          return {
            text: `${slug} - Photos ${formatSortTitle(sortTitle)}`,
          };
        },
        path: '/model/:modelName/:tab(photos)/:sortBy?/:page?',
        value: 'photos',
      },
      modelLatest: {
        breadcrumb: ({ match }: BreadcrumbParams) => {
          const slug = parseSlug(match.params.modelName);
          return {
            text: `${slug} Latest`,
          };
        },
        path: '/model/:modelName/:tab(latest)?/:page?',
        value: 'latest',
      },
      modelsCountry: {
        breadcrumb: ({ match }: BreadcrumbParams) => {
          const sortTitle = getSortTitle(MODEL_SORT_TYPES, match.params.sortBy);
          return {
            text: `${parseSlug(match.params.country)} ${formatSortTitle(sortTitle)}`,
          };
        },
        path: '/models/country/:country/:sortBy?/:page?',
        value: 'Models',
      },
      modelsCountryAlphabetical: {
        breadcrumb: ({ match }: BreadcrumbParams) => {
          const sortTitle = match.params.sortBy && getSortTitle(MODEL_SORT_TYPES, 'all');
          return {
            text: `${parseSlug(match.params.country)} ${formatSortTitle(sortTitle)}`,
          };
        },
        path: '/models/country/:country/all/:firstNameLetter?/:page?',
        value: 'Models',
      },
      models: {
        breadcrumb: ({ match }: BreadcrumbParams) => {
          const sortTitle = getSortTitle(MODEL_SORT_TYPES, match.params.sortBy);

          if (!sortTitle) {
            return null;
          }
          return {
            text: sortTitle,
          };
        },
        path: '/models/:sortBy?/:page?',
        value: 'Models',
      },
      modelsUserRated: {
        breadcrumb: ({ match }: BreadcrumbParams) => {
          const sortTitle = getSortTitle(MODEL_SORT_TYPES, match.params.sortBy);
          return {
            text: sortTitle || '',
          };
        },
        path: '/models/my-top/:rating?/:page?',
        value: 'Models',
      },
      modelsAlphabetical: {
        breadcrumb: ({ match }: BreadcrumbParams) => {
          const sortTitle = getSortTitle(MODEL_SORT_TYPES, match.params.sortBy);
          return {
            text: sortTitle || '',
          };
        },
        path: '/models/all/:firstNameLetter?/:page?',
        value: 'Models',
      },
      photographer: {
        breadcrumb: ({ match }: BreadcrumbParams) => {
          const sortTitle = getSortTitle(MOVIE_SORT_TYPES, match.params.sortBy);

          const { tab } = match.params;
          return [
            {
              text: `${parseSlug(match.params.photographerName)} ${tab ? `- ${capFirst(tab)}` : ''} ${formatSortTitle(
                sortTitle,
              )}`,
            },
          ];
        },
        path: '/photographer/:photographerName/:tab(photos|movies)?/:sortBy?/:page?',
        value: 'Photographer',
      },
      photographerLatest: {
        breadcrumb: ({ match }: BreadcrumbParams) => {
          const { tab } = match.params;
          return [
            {
              text: `${parseSlug(match.params.photographerName)} ${tab ? `- ${capFirst(tab)}` : ''}`,
            },
          ];
        },
        path: '/photographer/:photographerName/:tab(latest)?',
      },
      photographers: {
        breadcrumb: ({ match }: BreadcrumbParams) => {
          const sortTitle = getSortTitle(PHOTOGRAPHER_SORT_TYPES, match.params.sortBy);

          if (!sortTitle) {
            return null;
          }

          return {
            text: `Photographers ${formatSortTitle(sortTitle)}`,
          };
        },
        path: '/photographers/:sortBy?/:page?',
        value: 'Photographers',
      },
    },
  },
  movies: {
    breadcrumb: {
      text: 'Films',
      url: '/erotic-films',
    },
    oldPath: '/movies',
    path: '/erotic-films',
    routes: {
      movie: {
        breadcrumb: ({ state }: BreadcrumbParams) => {
          const movie = state.movie.item;
          if (movie) {
            return [
              {
                node: modelLinks(movie.models, movie.isStaffSelection || movie.isIntimateSelection),
              },
              {
                text: movie.name,
              },
            ];
          }
          return false;
        },
        path: '/model/:modelName/movie/:date/:movieName',
      },
      staffSelection: {
        breadcrumb: {
          text: 'Staff Selection',
        },
        path: '/erotic-films/staff/:page?',
        value: 'Staff Selection',
      },
      metartIntimate: {
        breadcrumb: {
          text: `MetArt Intimate`,
        },
        path: '/erotic-films/intimate/:sortBy?/:page?',
        value: 'MetArt Intimate',
      },
      fanFiction: {
        breadcrumb: ({ match }: BreadcrumbParams) => {
          const sortTitle = getSortTitle(MOVIE_SORT_TYPES, match.params.sortBy);
          return {
            text: `Fan Fiction ${formatSortTitle(sortTitle)}`,
          };
        },
        path: '/erotic-films/fan-fiction/:sortBy?/:page?',
        value: 'Fan Fiction',
      },
      myMovies: {
        breadcrumb: ({ match }: BreadcrumbParams) => {
          const sortTitle = getSortTitle(MOVIE_SORT_TYPES, match.params.sortBy);
          return {
            text: `My Movies ${formatSortTitle(sortTitle)}`,
          };
        },
        path: '/erotic-films/my/:sortBy?/:page?',
        value: 'PPSMovies',
      },

      movies: {
        breadcrumb: ({ match }: BreadcrumbParams) => {
          const sortTitle = getSortTitle(MOVIE_SORT_TYPES, match.params.sortBy);

          if (!sortTitle) {
            return null;
          }

          return {
            text: sortTitle,
          };
        },
        path: '/erotic-films/:sortBy?/:page?',
        value: 'Movies',
      },
    },
  },
  updates: {
    breadcrumb: {
      text: 'Updates',
      url: '/updates',
    },
    path: '/updates',
    routes: {
      stream: {
        breadcrumb: {
          text: 'Stream',
        },
        path: '/updates',
        value: 'updates',
      },
      upcoming: {
        breadcrumb: {
          text: 'Upcoming',
        },
        path: '/updates/upcoming',
        value: 'upcoming',
      },
      archive: {
        breadcrumb: {
          text: 'Archive',
        },
        path: '/updates/archive',
        value: 'archive',
      },
      archiveDate: {
        breadcrumb: ({ match }: BreadcrumbParams) => [
          {
            text: 'Archive',
            url: '/updates/archive',
          },
          {
            text: moment(`${match.params.year}${match.params.month}`, 'YYYYMM').format('MMMM YYYY'),
          },
        ],
        path: '/updates/archive/:year/:month/:page?',
        value: 'archive',
      },
      staffSelections: {
        breadcrumb: ({ match }: BreadcrumbParams) =>
          getYearBreadcrumbs({ text: 'Staff Selections', url: '/updates/staff' }, match.params.year),
        path: '/updates/staff/:year?',
        value: 'staff',
      },
      metartIntimate: {
        breadcrumb: ({ match }: BreadcrumbParams) =>
          getYearBreadcrumbs({ text: 'MetArt Intimate', url: '/updates/intimate' }, match.params.year),
        path: '/updates/intimate/:year?',
        value: 'intimate',
      },

      oldArchive: { path: '/updates/archive/triple', value: 'archive' },
      oldArchive2: { path: '/archive/route', value: 'archive' },
    },
  },
  blog: {
    breadcrumb: {
      text: 'Blog',
      url: '/blog',
    },
    path: '/blog',
    routes: {
      blog: {
        breadcrumb: {
          text: 'Posts',
        },
        path: '/blog',
      },
      posts: {
        breadcrumb: ({ match }: BreadcrumbParams) =>
          match.params.pageOrDate?.toString().match(/\d{6}/) === null
            ? {
                text: 'Latest',
              }
            : {
                text: moment(match.params.pageOrDate, 'YYYYMMDD').format('MMMM YYYY'),
              },
        path: '/blog/:pageOrDate?',
      },
      category: {
        breadcrumb: ({ match }: BreadcrumbParams) => ({
          text: match.params.category,
        }),
        path: '/blog/category/:category/:page?',
      },
      post: {
        breadcrumb: ({ state }: BreadcrumbParams) => {
          const post = state.blogPost.item;
          if (post) {
            return {
              text: `${post.title.match(/(\w+\W*){0,3}/)?.[0]}...`,
            };
          }
          return null;
        },
        path: '/blog/:date/:name',
      },
    },
  },
  search: {
    path: '/search',
    routes: {
      advancedSearch: {
        breadcrumb: {
          text: 'Advanced Search',
        },
        path: '/search',
      },
      searchResults: {
        breadcrumb: ({ match }: BreadcrumbParams) => {
          const sortTitle = getSortTitle(SEARCH_SORT_TYPES, match.params.sortBy);
          return [
            {
              text: 'Search',
              url: '/search',
            },
            {
              text: `Search Results ${formatSortTitle(sortTitle)}`,
            },
          ];
        },
        path: '/search/:searchTerm/:sortBy?/:page?',
      },
    },
  },
  user: {
    breadcrumb: ({ match }: BreadcrumbParams) => [
      {
        text: `User - ${match.params.displayName}`,
        url: `/user/${match.params.displayName}/`,
      },
    ],
    path: '/user/:displayName/',
    routes: {
      comments: {
        breadcrumb: {
          text: 'Comments',
        },
        path: '/user/:displayName/comments',
      },
    },
  },
  cover: {
    path: '/cover/:galleryUUID',
  },
  leaderboard: {
    path: '/leaderboard/:tab/:timePeriod?',
  },
  categories: {
    path: '/tags',
    routes: {
      categoriesPage: {
        breadcrumb: {
          text: 'Categories',
          url: '/tags',
        },
        path: '/tags',
      },
      categoriesResults: {
        breadcrumb: ({ match, state }: BreadcrumbParams) => {
          const sortTitle = getSortTitle(SEARCH_SORT_TYPES, match.params.sortBy);
          return [
            {
              text: 'Tags',
              url: '/tags',
            },
            {
              text: `${state.searchResults.displayName || 'Tag Results'} ${formatSortTitle(sortTitle)}`,
            },
          ];
        },
        path: '/tags/:searchTerm/:sortBy?/:page?',
      },
    },
  },
  favoritesAndRatings: {
    path: ['/favorites', '/ratings'],
    routes: {
      favorites: {
        breadcrumb: ({ match }: BreadcrumbParams) =>
          getTypeBreadcrumbs({ text: 'Favorites', url: '/favorites' }, match.params.type),
        path: '/favorites/:type?/:page?',
      },
      ratings: {
        breadcrumb: ({ match }: BreadcrumbParams) =>
          getTypeBreadcrumbs({ text: 'Ratings', url: '/ratings' }, match.params.type),
        path: '/ratings/:type?/:page?',
      },
    },
  },
  settings: {
    path: '/settings',
  },
  2257: {
    path: '/2257',
  },
  piracy: {
    path: '/piracy',
  },
  trustedPartners: {
    path: '/trusted-partners',
  },
  login: {
    path: '/login',
  },
  signup: {
    path: '/signup',
  },
  specialFeatures: {
    path: '/category/:category/:subCategory?/:extraCategory?/:page?',
  },
  subscriptionPreview: {
    path: '/subscription/preview/:token',
  },
  newsletterConfirmed: {
    path: '/newsletter/confirmed',
  },
  report: {
    path: ['/report-abuse', '/report-content'],
    routes: {
      abuse: {
        path: '/report-abuse',
      },
    },
  },
  ageVerificationCallback: {
    path: '/age-verification-callback',
  },
};

export const updatesRoutes = [
  routes.updates.routes.stream,
  routes.updates.routes.upcoming,
  routes.updates.routes.archive,
  routes.updates.routes.archiveDate,
  routes.updates.routes.staffSelections,
  routes.updates.routes.metartIntimate,
  { path: routes.updates.path },
];

export const galleriesRoutes = [
  routes.galleries.routes.gallery,
  routes.galleries.routes.myGalleries,
  routes.galleries.routes.galleries,
  routes.galleries.routes.images,
  routes.galleries.routes.botd,
  { path: routes.galleries.path },
];

export const moviesRoutes = [
  routes.movies.routes.movie,
  routes.movies.routes.staffSelection,
  routes.movies.routes.fanFiction,
  routes.movies.routes.myMovies,
  routes.movies.routes.metartIntimate,
  routes.movies.routes.movies,
  { path: routes.movies.path },
];

export const modelsAndArtistsRoutes = [
  routes.modelsAndArtists.routes.modelLatest,
  routes.modelsAndArtists.routes.modelPhotos,
  routes.modelsAndArtists.routes.modelMovies,
  routes.modelsAndArtists.routes.modelsAlphabetical,
  routes.modelsAndArtists.routes.modelsCountryAlphabetical,
  routes.modelsAndArtists.routes.modelsCountry,
  routes.modelsAndArtists.routes.models,
  routes.modelsAndArtists.routes.photographer,
  routes.modelsAndArtists.routes.photographerLatest,
  routes.modelsAndArtists.routes.photographers,
  { path: routes.modelsAndArtists.path },
];

export const blogRoutes = [
  routes.blog.routes.blog,
  routes.blog.routes.posts,
  routes.blog.routes.category,
  routes.blog.routes.post,
  { path: routes.blog.path },
];
